import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { Login } from "./routes/Login";
import { Dashboard } from "./routes/Dashboard";
import { Customers } from "./routes/Customers";
import { Leaderboard } from "./routes/Leaderboard";
import { Activity } from "./routes/Activity";
import { Redeem } from "./routes/Redeem";
import { Tag } from "./routes/Tag";
import { OwnerRedeem } from "./routes/OwnerRedeem";
import { useEffect, useState } from "react";
import { api } from "./lib/api";

type Session = { merchant_id: string; owner_phone: string; expires_at: number } | null;

function Shell({ children }: { children: React.ReactNode }) {
  const loc = useLocation();
  const tab = (to: string, label: string) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `px-3 py-2 rounded-md text-sm font-medium ${
          isActive ? "bg-punchy-500 text-white" : "text-neutral-700 hover:bg-neutral-200"
        }`
      }
    >
      {label}
    </NavLink>
  );
  return (
    <div className="min-h-full">
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2 flex-wrap">
          <div className="font-semibold mr-4">🥊 Punchy admin</div>
          <nav className="flex gap-1 flex-wrap">
            {tab("/dashboard", "Overview")}
            {tab("/customers", "Customers")}
            {tab("/leaderboard", "Leaderboard")}
            {tab("/activity", "Activity")}
            {tab("/redeem", "Redeem")}
            {tab("/tag", "Tag")}
          </nav>
          <div className="ml-auto">
            <button
              onClick={() => api("/api/logout", { method: "POST" }).finally(() => (location.href = "/"))}
              className="text-sm text-neutral-500 hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main key={loc.pathname} className="max-w-5xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<"loading" | "ok" | "no">("loading");
  useEffect(() => {
    api<Session>("/api/session")
      .then((s) => setState(s ? "ok" : "no"))
      .catch(() => setState("no"));
  }, []);
  if (state === "loading") {
    return <div className="p-6 text-neutral-500">Loading…</div>;
  }
  if (state === "no") {
    return (
      <div className="max-w-md mx-auto mt-20 p-6 text-center">
        <h1 className="text-xl font-semibold">Not signed in</h1>
        <p className="mt-2 text-neutral-600">
          Open WhatsApp and send the word <code className="bg-neutral-100 px-1.5 py-0.5 rounded">ADMIN</code> to the
          Punchy bot from your registered owner phone. You'll get a magic link valid for 30 minutes.
        </p>
      </div>
    );
  }
  return <Shell>{children}</Shell>;
}

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/redeem/:code" element={<Redeem />} />

      {/* Authed */}
      <Route path="/dashboard" element={<RequireAuth><Dashboard /></RequireAuth>} />
      <Route path="/customers" element={<RequireAuth><Customers /></RequireAuth>} />
      <Route path="/leaderboard" element={<RequireAuth><Leaderboard /></RequireAuth>} />
      <Route path="/activity" element={<RequireAuth><Activity /></RequireAuth>} />
      <Route path="/redeem" element={<RequireAuth><OwnerRedeem /></RequireAuth>} />
      <Route path="/tag" element={<RequireAuth><Tag /></RequireAuth>} />

      {/* Default */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
